import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import { useState } from "react";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch } from "react-redux";
import Loading from "../../../../../../components/loading/loading";
import { getClientDocumentsRequest } from "../../../../../../redux/clients/slice";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { uploadClientDocumentRequest } from "../../../../../../utils/APICalls";

type props = {
  showDialog: boolean;
  closeDialog: any;
  data?: any;
};

const styles = {
  headerTxt: {
    fontWeight: "540",
    letterSpacing: "0.6px",
    fontSize: 20,
  },
  closeBtn: {
    position: "absolute",
    right: 12,
    top: 12,
    color: "rgba(0, 0, 0, 0.54)",
  },
  layout: {
    width: "auto",
    mt: -2,
  },
  message: {
    color: "rgba(0, 0, 0, 0.87)",
    mb: 0.5,
  },
  actionBtn: {
    ml: 2,
    mr: 1,
  },
  buttonLayout: {
    my: 0.5,
  },
  buttonTxt: {
    fontWeight: "510",
    letterSpacing: "0.5px",
    fontSize: 14,
  },
};

export default function AddDocument({ showDialog, closeDialog, data }: props) {
  const [selectedFile, setSelectedFile] = useState<any>();
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setError(false);
    const file = event.target.files?.[0];
    setSelectedFile(file);
    if (!file) return;
    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ];
    if (!allowedTypes.includes(file.type)) {
      setError(true);
      setErrorMessage(
        "Unsupported file type. Only PDF, DOC, DOCX, JPG, or PNG files are allowed."
      );
      return;
    }
    if (!isFileSizeValid(file)) {
      return;
    }
  };

  const handleDelete = () => {
    setSelectedFile(null);
    setError(false);
    setErrorMessage("");
  };

  const isFileSizeValid = (file: any) => {
    if (file?.size > 10000000) {
      setError(true);
      setErrorMessage("File size exceeds 10MB limit.");
      return false;
    }
    return true;
  };

  const onSaveChanges = async () => {
    if (!selectedFile) {
      setError(true);
      setErrorMessage("Please select a file to save.");
      return;
    }

    if (!isFileSizeValid(selectedFile)) {
      return;
    }

    setLoading(true);
    const response = await uploadClientDocumentRequest(data?.id, selectedFile);
    setLoading(false);
    if (response?.status === 204) {
      dispatch(
        getClientDocumentsRequest({ id: data?.id, page: 1, pageSize: 10 })
      );
      closeDialog();
    } else if (response?.status === 400) {
      setError(true);
      setErrorMessage(response.data?.detail);
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={showDialog}
      onClose={closeDialog}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title" sx={styles.headerTxt}>
        Client - Documents - Add Document
      </DialogTitle>
      <Divider />
      <IconButton aria-label="close" onClick={closeDialog} sx={styles.closeBtn}>
        <CloseIcon />
      </IconButton>
      <DialogContent sx={styles.layout}>
        <Box
          sx={{
            mt: 2,
            height: 80,
            width: "100%",
            border: "1px dashed rgba(0, 0, 0, 0.12)",
            position: "relative",
            display: "flex",
            alignItems: "center",
          }}
        >
          <UploadFileIcon sx={{ ml: 5 }} color="primary" />
          <Box ml={2}>
            <Box sx={{ display: "flex" }}>
              <Typography color="primary" sx={{ textDecoration: "underline" }}>
                Click to upload
              </Typography>
              <Typography sx={{ ml: 1 }}>or drag and drop</Typography>
            </Box>
            <Typography sx={{ color: "rgba(0, 0, 0, 0.6)" }}>
              PDF, DOC, DOCX, JPG or PNG (max. 10MB)
            </Typography>
          </Box>
          <input
            style={{
              display: "block",
              height: 80,
              width: "100%",
              position: "absolute",
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              opacity: 0,
              cursor: "pointer",
            }}
            type="file"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/jpg,image/png"
            onChange={handleFileChange}
            name="logo"
            onClick={(event) => {
              // same file selection issue
              (event.target as HTMLInputElement).value = "";
            }}
          />
        </Box>
        {selectedFile && (
          <Box
            sx={{
              mt: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              border: "1.5px solid green",
              p: 0.8,
              borderRadius: 1,
              backgroundColor: "#F7FAF7",
            }}
          >
            <Box display={"flex"} alignItems={"center"}>
              <AttachFileOutlinedIcon color="primary" />
              <Box sx={{ ml: 1.5 }}>
                <Typography>
                  {selectedFile?.name || "No file selected"}
                </Typography>
                <Typography>
                  {" "}
                  {selectedFile
                    ? `${(selectedFile.size / 1024).toFixed(2)} kb`
                    : ""}
                </Typography>
              </Box>
            </Box>
            <Box>
              <IconButton aria-label="delete" onClick={handleDelete}>
                <DeleteOutlinedIcon color="primary" />
              </IconButton>
            </Box>
          </Box>
        )}
        {error && (
          <Typography sx={{ fontSize: 12, mt: 0.5 }} color="error">
            {errorMessage}
          </Typography>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={styles.buttonLayout}>
        <Button variant="outlined" onClick={closeDialog}>
          <Typography sx={styles.buttonTxt}>Cancel</Typography>
        </Button>
        <Button
          sx={styles.actionBtn}
          variant="contained"
          disabled={!selectedFile || error}
          onClick={onSaveChanges}
          autoFocus
        >
          <Typography sx={styles.buttonTxt}>Save Changes</Typography>
        </Button>
      </DialogActions>
      {loading && <Loading />}
    </Dialog>
  );
}
